import { sendEvent as sendGAEvent } from '../../analytics/ga';
import { isEmpty } from '../../util/array';
import { isNumber } from '../../util/numbers';
import { isNil, isNotNil, isString } from '../../util/object';
import { captureLog } from '../../util/sentry';
import { LOG_LEVELS } from '../debug/sentry/constants';
import Log from '../../model/Log';

let isSendingEvents = false;
const eventQueue = [];

const sendEvent = (eventCategory, eventAction, eventLabel, fieldsObject) => {
  return new Promise((resolve) => {
    sendGAEvent(eventCategory, eventAction, eventLabel, {
      ...fieldsObject,
      hitCallback: resolve,
    });
  });
};

/** Sends events until there are none left in the queue. */
const sendEventFromQueue = () => {
  if (isEmpty(eventQueue)) {
    isSendingEvents = false;
    return;
  }

  isSendingEvents = true;
  const {
    eventCategory,
    eventAction,
    eventLabel,
    fieldsObject = {},
  } = eventQueue.shift();

  sendEvent(eventCategory, eventAction, eventLabel, fieldsObject).then(() => {
    // Using request animation frame to limit the event QPS
    requestAnimationFrame(sendEventFromQueue);
  });
};

// GA fields can either be null, numbers, or strings.
const isValidGAFieldType = (val) =>
  isNil(val) || isNumber(val) || isString(val);

/** Adds the event to a queue. Events will be sent automatically, once per animation frame. */
export const enqueueGAEvent = (
  eventCategory,
  eventAction,
  eventLabel,
  fieldsObject,
) => {
  const gaEvent = {
    eventCategory,
    eventAction,
    eventLabel,
  };

  if (
    !isValidGAFieldType(eventCategory) ||
    !isValidGAFieldType(eventAction) ||
    !isValidGAFieldType(eventLabel)
  ) {
    const log = new Log(
      'Invalid type for GA event. Expected type null, number, or string.',
      LOG_LEVELS.ERROR,
      gaEvent,
    );
    captureLog(log);
    throw new Error(
      `Invalid type for GA event ${gaEvent}. Expected type null, number, or string.`,
    );
  }

  // Don't waste memory if there's no fieldsObject to enqueue.
  if (isNotNil(fieldsObject)) {
    gaEvent.fieldsObject = fieldsObject;
  }

  eventQueue.push(gaEvent);

  if (isSendingEvents) return;

  sendEventFromQueue();
};
